<script setup>
import CustomerTasksActionsCell from "@frontJs/components/tables/cells/projectManagement/tasks/tenantReports/CustomerTaskActionsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lastLoginDate",
      header: () => t("label.lastLoginDate"),
      cell: (props) =>
        props.row.original.lastLoginDate
          ? h(TextCellV2, {
            text: formatDate(props.row.original.lastLoginDate, false, true),
          })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "lastDoneTaskAt",
      header: () => t("label.lastDoneTaskAt"),
      cell: (props) =>
        props.row.original.lastDoneTaskAt
          ? h(TextCellV2, {
            text: formatDate(props.row.original.lastDoneTaskAt),
          })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "oldNotDoneTaskCreatedAt",
      header: () => t("label.oldNotDoneTaskCreatedAt"),
      cell: (props) =>
        props.row.original.oldNotDoneTaskCreatedAt
          ? h(TextCellV2, {
            text: formatDate(props.row.original.oldNotDoneTaskCreatedAt),
          })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "taskToProcessCount",
      header: () => t("label.taskToProcessCount"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.taskToProcessCount,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "maxCustomerCommentAt",
      header: () => t("label.lastCommentAt"),
      cell: (props) =>
        props.row.original.maxCustomerCommentAt
          ? h(TextCellV2, {
            text: formatDate(props.row.original.maxCustomerCommentAt),
          })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "taskReminderEmailSentAt",
      header: () => t("label.taskReminderEmailSentAt"),
      cell: (props) =>
        props.row.original.taskReminderEmailSentAt
          ? h(TextCellV2, {
            text: formatDate(props.row.original.taskReminderEmailSentAt),
          })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(CustomerTasksActionsCell, {
          row: innerProps.row,
          callback: updateData,
        }),
    }),
  ],
  url: route("front.projectManagement.task.postCustomerTasks"),
  columnPinning: {
    right: ['actions']
  }
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    />
  </div>
</template>
